<template>
  <div class="table-container">
    <el-table
      lazy
      :row-key="rowKey"
      :data="rawData"
      :highlight-current-row="hightlight"
      :max-height="maxHeight"
      ref="table"
      empty-text="no data!"
      border
      :stripe="stripe"
      :row-class-name="tableRowClassName"
      :header-row-style="{ height: headerHeight + 'px' }"
      :row-style="{ height: rowHeight + 'px' }"
      :expand-row-keys="expands"
      @row-click="selectOneRow"
      @sort-change="sortMethod"
      :default-sort="defaultSort"
      :show-summary="summary"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="expand" v-if="hasRowExpand">
        <template slot-scope="scope">
          <slot
            name="row-expand"
            v-bind:row="scope.row"
            v-bind:index="getRowIndex(scope.$index)"
          />
        </template>
      </el-table-column>
      <el-table-column
        type="selection"
        width="55"
        :reserve-selection="true"
        v-if="selectable"
      >
      </el-table-column>
      <el-table-column
        v-for="(header, index) in tableHeaders"
        :key="'tabel-header-' + index"
        :prop="header.key"
        :label="header.label"
        :min-width="header.width"
        :sortable="header.sortable"
      >
        <template slot-scope="scope">
          <el-tag
            class="basic-table_tag"
            size="small"
            v-if="header.type === T.Tag && get(scope.row, header.key)"
            :type="getTagType(header, get(scope.row, header.key))"
            :effect="header.effect"
          >
            {{ get(scope.row, header.key) }}
          </el-tag>
          <div
            class="basic-table_timestamp f-c"
            v-else-if="header.type === T.Time"
          >
            <span> {{ T.TimeSimple(get(scope.row, header.key)) }} </span>
          </div>
          <el-progress
            :text-inside="true"
            :stroke-width="26"
            :percentage="get(scope.row, header.key)"
            v-else-if="header.type === T.Progress"
          />
          <address-tag
            class="basic-address-tag"
            :address="get(scope.row, header.key)"
            v-else-if="header.type === T.AddressTag"
          />
          <hold-or-not
            :value="get(scope.row, header.key)"
            v-else-if="header.type === T.HoldTag"
          />
          <el-checkbox
            v-else-if="header.type === T.Checkbox"
            :value="get(scope.row, header.key)"
          />
          <div v-else-if="header.type === T.SlotType" class="basic-slot-class">
            <template>
              <slot
                :name="header.slotName"
                v-bind:row="scope.row"
                v-bind:index="getRowIndex(scope.$index)"
              >
              </slot>
            </template>
          </div>
          <div v-else-if="header.type === T.Link" class="f-r-c-c">
            <a
              class="table-href"
              target="_blank"
              @click="linkClick(header.link, get(scope.row, header.linkKey))"
            >
              {{ get(scope.row, header.key) }}
            </a>
            <svg-icon
              v-if="get(scope.row, header.key)"
              icon-class="jump"
              class="jump-icon"
              @click.native="
                linkClick(header.link, get(scope.row, header.linkKey), true)
              "
            />
          </div>

          <div
            class="basic-table_common-text"
            v-else
            :title="get(scope.row, header.key)"
          >
            {{ get(scope.row, header.key) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        label="Actions"
        v-if="hasActions"
        fixed="right"
        :min-width="actionMinWidth"
        :width="actionWidth"
      >
        <template slot-scope="scope">
          <slot
            name="row-actions"
            v-bind:row="scope.row"
            v-bind:index="getRowIndex(scope.$index)"
          />
        </template>
      </el-table-column>
    </el-table>
    <div class="basic-table-footer">
      <div class="pagination-left-ops">
        <slot name="footer-ops" />
      </div>
      <el-pagination
        v-if="showPagination"
        class="pagination"
        background
        :layout="pageLayout"
        :current-page="curPage"
        :page-size="pageSize"
        :page-sizes="T.DefaultPageSizes"
        :total="total"
        @current-change="
          value => paginationChange(T.PageCurrentKey, 'value', value)
        "
        @size-change="value => paginationChange(T.PageSizeKey, 'value', value)"
      />
    </div>
  </div>
</template>
<script>
// others
import get from 'lodash/get'
// types
import * as T from '@/core/configs/types'
import { pageJumps } from '@/core/mixin/pages'
import AddressTag from '@/components/address/AddressTag.vue'
import HoldOrNot from './HoldOrNot.vue'
export default {
  name: 'BasicTable',
  mixins: [pageJumps],
  components: { AddressTag, HoldOrNot },
  props: {
    tableHeaders: {
      type: Array,
      default: () => []
    },
    tableData: {
      type: Object,
      default: () => null
    },
    hasActions: {
      type: Boolean,
      default: false
    },
    hasRowExpand: {
      type: Boolean,
      default: false
    },
    selectable: {
      type: Boolean,
      default: false
    },
    handleSelectionChange: {
      type: Function,
      default: () => {}
    },
    paginationChange: {
      type: Function,
      default: () => {}
    },
    sortChange: {
      type: Function,
      default: () => {}
    },
    actionMinWidth: {
      type: Number,
      default: 80
    },
    actionWidth: {
      type: Number,
      default: 0
    },
    rowClassName: {
      type: Function,
      default: () => {}
    },
    hightlight: {
      type: Boolean,
      default: false
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    pageLayout: {
      type: String,
      default: () => {
        return 'total, sizes, prev, pager, next'
      }
    },
    checkExpand: {
      type: Function,
      default: () => false
    },
    headerHeight: {
      type: Number,
      default: 50
    },
    rowHeight: {
      type: Number,
      default: 60
    },
    maxHeight: {
      type: Number,
      default: -1
    },
    rowKey: {
      type: String,
      default: 'id'
    },
    summary: {
      type: Boolean,
      default: false
    },
    stripe: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      T,
      columnToType: {}
    }
  },
  computed: {
    rawData() {
      return this.tableData?.records ?? []
    },
    total() {
      return this.tableData?.total ?? 0
    },
    curPage() {
      return this.tableData?.current ?? 1
    },
    pageSize() {
      return this.tableData?.size ?? 20
    },
    expands() {
      return this.rawData
        .filter(one => this.checkExpand(one))
        .map(one => get(one, this.rowKey))
    },
    defaultSort() {
      const defaultSort = this.tableHeaders.filter(one => one.defaultSort)
      if (defaultSort.length === 0) return {}
      return {
        prop: defaultSort[0].key,
        order: defaultSort[0].defaultOrder ?? T.Ascending
      }
    }
  },
  watch: {
    tableHeaders: {
      handler() {
        const curr = {}
        this.tableHeaders.forEach(e => {
          curr[e.key] = e.type
        })
        this.$set(this, 'columnToType', curr)
      },
      immediate: true
    }
  },
  onUpdated() {},
  methods: {
    get,
    getRowIndex(index) {
      return (this.curPage - 1) * this.pageSize + index
    },
    tableRowClassName({ row, rowIndex }) {
      return this.rowClassName(row, rowIndex)
    },
    setCurrentRow(row) {
      this.$refs.table.setCurrentRow(row)
    },
    selectOneRow(row) {
      this.$emit('selectedRow', row)
    },
    getTagType(header, val) {
      switch (typeof header.tagType) {
        case 'string':
          return header.tagType
        case 'function':
          return header.tagType(val)
        default:
          return null
      }
    },
    sortMethod({ column, prop, order }) {
      const index = this.tableHeaders.findIndex(one => one.key === prop)
      if (index > -1) {
        this.sortChange({
          col: this.tableHeaders[index].col,
          order: order,
          index
        })
      }
    },
    linkClick(methodName, params, newTab = false) {
      this[methodName](params, null, { newTab })
    }
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.el-table ::v-deep th {
  font-size: $title-level-regular !important;
  padding: 5px 0 !important;

  .cell {
    padding: 0 5px !important;
    text-align: center;
  }
}

.el-table ::v-deep td {
  padding: 8px 0 !important;

  .cell {
    padding: 0 10px !important;
    width: 100%;

    @extend .f-r-c-c;
  }
}

.el-table ::v-deep.error-row {
  background-color: $table-row-danger-color !important;
}

.el-table ::v-deep.success-row {
  background-color: $table-row-success-color !important;
}

.el-table ::v-deep.el-table__fixed-right {
  height: 100% !important;
}

.el-table ::v-deep .table-href {
  text-decoration: underline;
  color: $business-color;

  &:hover {
    cursor: pointer;
    // color: rgba($el-primary-color, 1);
  }
}

.basic-table_timestamp {
  // font-size: $regular-body-font-size;
  text-align: center;
}

.basic-address-tag {
  /* max-height: 48px !important; */
  height: 100% !important;
  overflow: hidden;
}

.basic-slot-class {
  width: 100%;
}

.basic-table_common-text {
  text-overflow: ellipsis;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.table-container {
  border-radius: 3px;

  .basic-table-footer {
    width: 100%;
    justify-content: flex-end;
    gap: 20px;

    @extend .f-r-vc;
    @extend .f-r-hc;

    .pagination {
      margin-top: 10px;
      ::v-deep .el-pagination {
        .el-pagination__sizes {
          .el-select {
            .el-input {
              .el-input__inner {
                border-top: none !important;
                border-left: none !important;
                border-right: none !important;
                border-radius: 0 !important;
              }
            }
          }
        }
      }

      .el-button {
        border: none;
      }
    }
  }
}

.el-progress {
  width: 90%;
}

::v-deep .el-table__body tr.current-row > td {
  background-color: $table-current-row-color !important;
}

.el-table {
  width: 99.9% !important;
}

.el-table ::v-deep .el-table__expand-icon {
  width: 32px;
  height: 32px;
}

.jump-icon {
  font-size: 18px;

  &:hover {
    cursor: pointer;
  }
}
</style>
